/* Prezentační režim Analýzy V2+ — kroky, otázky a taháky poradce.
 *
 * PROČ SAMOSTATNÝ MODUL:
 * AnalyzaV2Page.js má přes 2000 řádků. Kroky a scénáře jsou to jediné, co si
 * poradce reálně pamatuje a co se mění nejčastěji — mít je jako čistá data
 * znamená, že jdou otestovat bez mountování celé stránky a že se text dá
 * upravit bez zásahu do UI.
 *
 * PRVNÍ SCHŮZKA JE SBĚR DAT, NE PRODEJ.
 * Proto tu NENÍ krok „Doporučení" — konkrétní návrh patří až na
 * /crm/clients/:cid/navrh. Kroků je deset: devět sběrných a před nimi
 * Představení (29. 9. 2026).
 *
 * TEXTY JSOU VE SLOVNÍKU, NE TADY (dráha A2, 29. 9. 2026).
 * Do té doby tu stály dvě tabulky — česká a slovenská — a každý jiný jazyk
 * spadl do české větve. Vietnamský poradce tak měl celou prezentaci česky
 * (čipy, otázky, taháky), přeložené bylo jen Představení. Teď modul drží jen
 * KLÍČE a pořadí; popisek, otázku, tahák i tip bere z pětijazyčného slovníku
 * (blok `analyzaPrezentace`, Představení `analyzaProdej.prez.predstaveni`)
 * přes textJazyka — čistá funkce, takže to jde dál testovat bez Reactu.
 * Nový text se přidává do slovníku (scratchpad/slovnik.py), ne sem.
 */
import { textJazyka } from "@/lib/i18n/textMimoReact";
/* Šířky sloupců kompaktní tabulky Zdrojů — z nich se počítá, kolik místa musí
 * levý panel kroku Zdroje mít vždy (viz prezLayout). */
import { sloupceZdroje } from "./zdrojeSloupce";
import { KATEGORIE } from "./produktyPole";

/* Kořen klíčů kroku ve slovníku. Představení má texty z dřívější vlny
 * (analyzaProdej), ostatní kroky blok analyzaPrezentace. `say` = kolik
 * odstavců taháku krok má (klíče say1…sayN), `tip` = klíč metodické poznámky. */
const KROKY = [
  // Krok Čas: tahák „Co je pro vás důležité vědět o tom, jak pracuji?“ pryč (majitel 20. 10. 2026) — vlevo jen „Ano, má čas“.
  { key: "predstaveni", koren: "analyzaProdej.prez.predstaveni", say: [], tip: "tip" },
  { key: "rodina", koren: "analyzaPrezentace.krok.rodina", tahak: "analyzaPrezentace.tahak.rodina", say: ["say1", "say2"], tip: "tip" },
  { key: "ocekavani", koren: "analyzaPrezentace.krok.ocekavani", tahak: "analyzaPrezentace.tahak.ocekavani", say: ["say1", "say2"], tip: "tip" },
  { key: "hodnoty", koren: "analyzaPrezentace.krok.hodnoty", tahak: "analyzaPrezentace.tahak.hodnoty", say: ["say1", "say2", "say3"], tip: "tip" },
  { key: "cile", koren: "analyzaPrezentace.krok.cile", tahak: "analyzaPrezentace.tahak.cile", say: ["say1", "say2", "say3"], tip: "tip" },
  { key: "zdroje", koren: "analyzaPrezentace.krok.zdroje", tahak: "analyzaPrezentace.tahak.zdroje", say: ["say1"], tip: "tip" },
  { key: "zavazky", koren: "analyzaPrezentace.krok.zavazky", tahak: "analyzaPrezentace.tahak.zavazky", say: ["say1", "say2"], tip: "tip" },
  { key: "prijmy", koren: "analyzaPrezentace.krok.prijmy", tahak: "analyzaPrezentace.tahak.prijmy", say: ["say1", "say2"], tip: "tip" },
  { key: "rizika", koren: "analyzaPrezentace.krok.rizika", tahak: "analyzaPrezentace.tahak.rizika", say: ["say1", "say2"], tip: "tip" },
  { key: "zaver", koren: "analyzaPrezentace.krok.zaver", tahak: "analyzaPrezentace.tahak.zaver", say: ["say1", "say2", "say3"], tip: "tip" },
];

/** Kroky prezentace v jazyce poradce: { key, tab (čip), q (otázka) }. */
export function prezSteps(lang) {
  return KROKY.map(({ key, koren }) => ({
    key,
    tab: textJazyka(lang, `${koren}.tab`),
    q: textJazyka(lang, `${koren}.q`),
  }));
}

/** Pořadí kroků prezentace s českými texty (zdroj pravdy je slovník). */
export const PREZ_STEPS = prezSteps("cs");

export const PREZ_STEP_KEYS = PREZ_STEPS.map((s) => s.key);

/* Poměr levého (poradce) a pravého (klient) panelu podle kroku.
 *
 * PROČ ne jeden pevný poměr: kroky se liší tím, kolik se zapisuje.
 * U Hodnot poradce píše dvě věty a klient kouká na velká slova → pravá strana
 * dostane víc. U Zdrojů a Závazků se vyplňuje spousta polí → levá strana by
 * při 40 % byla nepoužitelně úzká a datumy by se zalamovaly.
 *
 * Hodnota = šířka LEVÉHO panelu v procentech.
 */
/* UAT 8/2026: „i v tom prezenčním režimu, aby to šlo taky vyplnit."
 * U Zdrojů je vlevo TABULKA smluv — při 55 % se do panelu nevešla a poradce
 * by na schůzce musel posouvat myší doprava. 70 % je nejmenší poměr, při
 * kterém se kompaktní řádek (nejširší je penzijko) vejde i na 1280px displeji.
 * Pravý panel u Zdrojů nese jen souhrnný graf, ten se do 30 % vejde. */
/* A2.2 (audit 3. 10. 2026): PLÁTNO ZDROJŮ NA 40 %. Při 30 % se klientovi na
 * 1440 px uřízlo „13 400 Kč/měs“ a na tabletu se čísla slévala přes sebe.
 * Tabulka vlevo ale dál nesmí potřebovat posuvník (UAT 8/2026) — proto
 * levý panel Zdrojů dostane 60 dílů, ALE NIKDY MÍŇ, než kolik potřebuje
 * nejširší kompaktní řádek (`minLevyPx`, nejvýš 70 % okna). Na 1440 px tak
 * klient má 40 %, na 1280 px tabulka zůstane celá a plátno má, co zbude. */
/* Č11 (4. 10. 2026): RIZIKA NA 40 %. Levý panel Rizik už nemá žádné pole
 * (Ostatní rizika a Navrhované řešení pryč, tabulka majetkových rizik taky) —
 * zbyl tahák a sbalené námitky. Při 45 % zel prázdnotou, proto stejný poměr
 * jako ostatní kroky bez zápisu (Představení, Rodina, Očekávání, Hodnoty). */
const LAYOUT = {
  predstaveni: 40,
  rodina: 40, ocekavani: 40, hodnoty: 40,
  cile: 52, zdroje: 60, zavazky: 55, prijmy: 52,
  rizika: 40, zaver: 45,
};

/* Odsazení levého panelu (p-6 vlevo i vpravo) a koncové sloupce řádku
 * (stav, ⋯, koš) — tytéž konstanty jako měření v uat2Etapy.test.js. */
const ODSAZENI_PANELU = 48;
const KONEC_RADKU = 130;
/** Nejmenší šířka levého panelu Zdrojů, při které se vejde každý kompaktní řádek. */
export function minLevyZdrojePx() {
  const sirky = KATEGORIE.map((kat) => sloupceZdroje(kat, "Kč", { kompaktni: true }).reduce((a, s) => a + (+s.sirka || 0), 0) + KONEC_RADKU);
  return Math.max(0, ...sirky) + ODSAZENI_PANELU;
}

export function prezLayout(stepKey) {
  const left = LAYOUT[stepKey] || 45;
  const right = 100 - left;
  if (stepKey === "zdroje") {
    const minLeftPx = minLevyZdrojePx();
    return { left, right, minLeftPx, gridTemplate: `minmax(min(${minLeftPx}px, 70%), ${left}fr) minmax(0, ${right}fr)` };
  }
  return { left, right, gridTemplate: `${left}% ${right}%` };
}

/* ZÁSTUPNÉ SYMBOLY V TAHÁCÍCH (audit 29. 9. 2026).
 *
 * Taháky dřív obsahovaly doslovné „[částka]“ a „[klientova slova]“ a prezentace
 * je četla tak, jak byly — nováček četl „vychází vám přibližně [částka]“ a
 * uprostřed klíčové otázky hledal číslo. Běžný režim přitom tatáž místa
 * doplňoval. Teď:
 *   {castka}        = spočítané volné cash flow (bilance.js → volneCashflow),
 *   {slovaKlienta}  = co klient řekl v kroku Hodnoty (d.penize).
 * KDYŽ HODNOTA CHYBÍ, VĚTA SE VYNECHÁ CELÁ — žádné závorky ani prázdné místo.
 * Klíč symbolu je stejný ve všech jazycích; překládá se jen věta kolem. */
const ZASTUPNE = /\{(castka|slovaKlienta)\}/g;
const MA_ZASTUPNY = /\{(castka|slovaKlienta)\}/;

/** Dosadí hodnoty do jednoho řádku taháku; věta s chybějící hodnotou zmizí. */
export function dosadDoTahaku(radek, hodnoty = {}) {
  const text = String(radek ?? "");
  if (!MA_ZASTUPNY.test(text)) return text;
  const h = hodnoty && typeof hodnoty === "object" ? hodnoty : {};
  /* Dělí se ŠABLONA, ne výsledek — částka „21 500 Kč“ ani slova klienta
   * s tečkou tak větu nerozlomí. */
  const vety = text.match(/[^.?!]+[.?!]*\s*/g) || [text];
  return vety
    .map((veta) => {
      let chybi = false;
      const hotovo = veta.replace(ZASTUPNE, (_, klic) => {
        const v = h[klic];
        const t = v === null || v === undefined ? "" : String(v).trim();
        if (!t) chybi = true;
        return t;
      });
      return chybi ? "" : hotovo;
    })
    .join("")
    .trim();
}

/**
 * Tahák kroku v jazyce poradce. `hodnoty` = { castka, slovaKlienta } — co se
 * má dosadit. Bez hodnot se věty se zástupným symbolem vynechají (nikdy
 * „[částka]“). Neznámý krok = prázdný tahák.
 */
export function prezScript(key, lang, hodnoty = {}) {
  const krok = KROKY.find((k) => k.key === key);
  if (!krok) return { say: [], tip: "" };
  const koren = krok.tahak || krok.koren;
  return {
    say: krok.say
      .map((s) => dosadDoTahaku(textJazyka(lang, `${koren}.${s}`), hodnoty))
      .filter((r) => r !== ""),
    tip: textJazyka(lang, `${koren}.${krok.tip}`),
  };
}

/* Rychlé chipy k očekávání. DOPLŇUJÍ zápis, nenahrazují klientova slova —
 * proto se připojují k textu, ne že by ho přepsaly. Text chipu je v jazyce
 * poradce (připisuje se do jeho zápisu), klíče drží slovník. */
const CHIPY_OCEKAVANI = ["rezerva", "bydleni", "deti", "investice", "duchod", "pojisteni", "dluhy", "plan"];

export function ocekavaniChips(lang) {
  return CHIPY_OCEKAVANI.map((k) => textJazyka(lang, `analyzaPrezentace.chip.${k}`));
}
export const OCEKAVANI_CHIPS = ocekavaniChips("cs");
export const OCEKAVANI_CHIPS_SK = ocekavaniChips("sk");

/** Připojí chip k dosavadnímu textu bez duplicit a bez přepsání zápisu. */
export function pridatChip(text, chip) {
  const cur = (text || "").trim();
  if (!chip) return cur;
  const has = cur
    .split(/[\n,]+/)
    .map((s) => s.trim().toLowerCase())
    .includes(chip.toLowerCase());
  if (has) return cur;
  return cur ? `${cur}, ${chip}` : chip;
}

/* Formy příjmu — patří k Příjmům, ne k rodině.
 * FORMY_PRIJMU jsou HODNOTY V DATECH (viz níž), FORMY_PRIJMU_SK slovenské
 * hodnoty ze starších analýz, které se při načtení převádějí. Popisek na
 * obrazovce jde ze slovníku (`analyzaProdej.forma.*`) ve všech pěti jazycích. */
export const FORMY_PRIJMU = ["Zaměstnanec", "OSVČ", "Majitel firmy", "Jiný zdroj"];
export const FORMY_PRIJMU_SK = ["Zamestnanec", "SZČO", "Majiteľ firmy", "Iný zdroj"];
const KLICE_FOREM = ["zamestnanec", "osvc", "majitel", "jiny"];
export function formyPrijmu(lang) {
  return KLICE_FOREM.map((k) => textJazyka(lang, `analyzaProdej.forma.${k}`));
}

/* FORMA PŘÍJMU V DATECH = ČESKÝ POPISEK Z FORMY_PRIJMU (29. 9. 2026).
 *
 * Dřív se ukládal popisek v jazyce poradce — slovenský „SZČO“, český „OSVČ“ —
 * a vietnamský poradce viděl češtinu. Teď se do dat píše vždy JEDNA hodnota
 * (český popisek, jak ho mají všechny starší analýzy) a na obrazovce se
 * překládá přes slovník (`analyzaProdej.forma.*`). Slovenské hodnoty ze
 * starších analýz se při načtení převedou na tutéž hodnotu; cokoli jiného
 * (ručně psaný text z dávných verzí) zůstane beze změny — nic se nemaže.
 * Nezadáno = "" jako dosud. */
const FORMA_SK_NA_CS = Object.fromEntries(FORMY_PRIJMU_SK.map((sk, i) => [sk, FORMY_PRIJMU[i]]));

/** Uložená forma příjmu → hodnota z FORMY_PRIJMU (nebo původní text, nebo ""). */
export function normFormaPrijmu(v) {
  const s = String(v ?? "").trim();
  if (!s) return "";
  if (FORMY_PRIJMU.includes(s)) return s;
  return FORMA_SK_NA_CS[s] || s;
}

/* Podklady, které má klient dodat — checklist do závěru schůzky.
 * Tady jen klíče a pořadí; popisek je ve slovníku (`analyzaProdej.podklady.*`). */
export const PODKLADY = [
  { key: "pojistne" },
  { key: "investicni" },
  { key: "uverove" },
  { key: "vypisy" },
  { key: "dotaznik" },
];
export function podkladyList(lang) {
  return PODKLADY.map((p) => ({ key: p.key, label: textJazyka(lang, `analyzaProdej.podklady.${p.key}`) }));
}

/* ODKAZ DO DIÁŘE NA DALŠÍ SCHŮZKU — JEDEN pro formulář i prezentaci
 * (audit 29. 9. 2026).
 *
 * Tlačítko v závěru prezentace dřív poslalo do diáře jen kontakt: aktivita se
 * založila bez jména klienta a datum s časem, které poradce zapsal o dva řádky
 * výš, psal podruhé — před klientem. Diář (DiarPageV3) umí převzít `title`
 * i `start` („YYYY-MM-DD“ → 9:00, nebo „YYYY-MM-DDTHH:MM“).
 * Neplatné datum/čas se NEPOSÍLÁ — radši prázdné pole než vymyšlený termín. */
const RE_DATUM = /^\d{4}-\d{2}-\d{2}$/;
const RE_CAS = /^\d{2}:\d{2}$/;

/* KAM PO ZÁPISU TERMÍNU (29. 9. 2026, 2. kolo). Poradce v Závěru prezentace
 * zapne „Zapsat termín do diáře po prezentaci“ a pak klikne „Připravit návrh
 * řešení“. Dřív se šlo rovnou na /navrh a slíbený termín se ztratil. Teď jde
 * nejdřív do diáře s předvyplněnou schůzkou a diář po zápisu (nebo zavření
 * formuláře) pokračuje sem. Povolená je JEN vnitřní cesta ke klientovi
 * („/crm/clients/<id>/<výstup>“) — žádný cizí web ani „//host“ (open redirect). */
/* Kotva (15. 10. 2026, SPEC-STAVBA §2 blok 2): „Domluvit“ v Závěru se po zápisu
 * vrací rovnou k bloku podkladů. Jen dvě kotvy z whitelistu, nic jiného
 * (žádné `#cokoli`, `//`, `javascript:`) — hlídá navratZDiare2026_10_14.test.js. */
/* + přesně „/crm/calls“ (19. 10. 2026, nález V2): Schůzka ve Zrcadle volání
 * otevře zápis termínu do diáře a po uložení se vrátí k volání. */
const RE_NAVRAT = /^(?:\/crm\/calls|\/crm\/clients\/[A-Za-z0-9_-]+\/[a-z0-9-]+(?:#av-zaver-(?:podklady|doporuceni))?)$/;
export function bezpecnyNavratZDiare(potom) {
  return typeof potom === "string" && RE_NAVRAT.test(potom) ? potom : null;
}

export function odkazDoDiare({ cid, jmeno, datum, cas, nazev = "Schůzka", potom = null } = {}) {
  const kdo = String(jmeno ?? "").trim();
  const titul = kdo ? `${nazev} — ${kdo}` : String(nazev);
  let url = `/diar?newActivity=1&contact_id=${encodeURIComponent(String(cid ?? ""))}&type=schuzka&title=${encodeURIComponent(titul)}`;
  const den = String(datum ?? "").trim();
  if (RE_DATUM.test(den)) {
    const hod = String(cas ?? "").trim();
    url += `&start=${encodeURIComponent(RE_CAS.test(hod) ? `${den}T${hod}` : den)}`;
  }
  const navrat = bezpecnyNavratZDiare(potom);
  if (navrat) url += `&potom=${encodeURIComponent(navrat)}`;
  return url;
}
